import { useEffect, useState } from "react";
import { CheckCircle, Circle, CaretDown, CaretUp, PaperPlaneRight, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtFull = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", { weekday: "long", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};
const roleLabel = (r) => (r === "guru" ? "Umi Guru" : r === "admin" ? "Admin" : "Orang Tua");

export function KajianFeed({ videoId }) {
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [text, setText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/kajian/${videoId}/feed`).then((r) => setFeed(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [videoId]);

  const toggleWatched = async () => {
    setBusy(true);
    try { await api.post(`/kajian/${videoId}/watched`); await load(); } finally { setBusy(false); }
  };
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try { await api.post(`/kajian/${videoId}/comment`, { text: t }); setText(""); await load(); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/kajian/comment/${id}`); await load(); } catch {} };

  if (!feed) return <div className="px-3 pb-3 text-stone-400 text-xs">Memuat…</div>;

  const isParent = user?.role === "orangtua";
  const isAdmin = user?.role === "admin";
  const comments = feed.comments || [];
  const preview = expanded ? comments : comments.slice(0, 2);

  return (
    <div className="border-t border-stone-100 px-3 pt-3 pb-3.5" data-testid={`kajian-feed-${videoId}`}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        {isParent ? (
          <button data-testid={`kajian-watched-btn-${videoId}`} onClick={toggleWatched} disabled={busy}
            className={`flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5 transition-colors ${feed.my_watched ? "bg-primary text-white" : "bg-stone-100 text-stone-600"}`}>
            {feed.my_watched ? <CheckCircle size={18} weight="fill" /> : <Circle size={18} />}
            {feed.my_watched ? "Sudah ditonton" : "Tandai sudah nonton"}
          </button>
        ) : (
          <span className="text-xs text-stone-400">Checklist tontonan orang tua</span>
        )}
        <span className="text-xs text-stone-500 font-semibold">{feed.watched_count} sudah nonton</span>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input data-testid={`kajian-comment-input-${videoId}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Tulis komentar…"
          className="flex-1 min-w-0 bg-stone-50 border border-stone-200 rounded-full py-2 px-3.5 text-sm outline-none focus:border-primary" />
        <button data-testid={`kajian-comment-send-${videoId}`} onClick={send} disabled={busy || !text.trim()}
          className="shrink-0 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
          <PaperPlaneRight size={17} weight="fill" />
        </button>
      </div>

      <div className="space-y-2" data-testid={`kajian-comments-${videoId}`}>
        {comments.length === 0 && <p className="text-stone-400 text-xs">Belum ada komentar. Jadilah yang pertama.</p>}
        {preview.map((c) => (
          <div key={c.id} className="bg-stone-50 rounded-xl px-3 py-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-bold text-stone-700 min-w-0">{c.name} <span className="text-stone-400 font-normal">• {roleLabel(c.role)}</span></p>
              {isAdmin && (
                <button data-testid={`kajian-comment-del-${c.id}`} onClick={() => del(c.id)} className="shrink-0 text-stone-300 hover:text-red-500">
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
            <p className="text-sm text-stone-700 whitespace-pre-wrap break-words">{c.text}</p>
            <p className="text-[10px] text-stone-400 mt-0.5">{fmtFull(c.at)}</p>
          </div>
        ))}
      </div>

      {(comments.length > 2 || feed.watched_count > 0) && (
        <button data-testid={`kajian-expand-${videoId}`} onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-primary text-xs font-bold flex items-center gap-1">
          {expanded ? <>Sembunyikan <CaretUp size={13} weight="bold" /></> : <>Lihat semua komentar &amp; daftar tontonan <CaretDown size={13} weight="bold" /></>}
        </button>
      )}

      {expanded && (feed.watched || []).length > 0 && (
        <div className="mt-3" data-testid={`kajian-watched-list-${videoId}`}>
          <p className="text-xs font-bold text-stone-600 mb-1.5">Sudah menonton ({feed.watched_count})</p>
          <div className="space-y-1">
            {feed.watched.map((w, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
                <CheckCircle size={14} weight="fill" className="text-primary shrink-0" />
                <span className="font-semibold">{w.name}</span>
                <span className="text-stone-400">• {fmtFull(w.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
